import { useMemo, useSyncExternalStore } from 'react';
import { feedbackConversationAuthorityFingerprint, isCurrentRecapScope } from '@/lib/api';
import type { RecipientFeedbackCollectionQuery } from '@/lib/types';
import { useCanonicalCollectionScope } from './useCanonicalFeedback';

export type RecipientCollectionControls = Omit<RecipientFeedbackCollectionQuery, 'player_id' | 'cursor' | 'limit'> & { limit: 20 | 50 };
export type RecipientCollectionNavigation = Readonly<{
  controls: RecipientCollectionControls;
  cursors: readonly (string | undefined)[];
  scrollTop: number;
  refreshVersion: number;
}>;
const initial = (): RecipientCollectionNavigation => ({ controls: { limit: 20 }, cursors: [undefined], scrollTop: 0, refreshVersion: 0 });
function cell() {
  let value = initial();
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    subscribe: (notify: () => void) => { listeners.add(notify); return () => { listeners.delete(notify); }; },
    update: (change: (previous: RecipientCollectionNavigation) => RecipientCollectionNavigation) => {
      const next = change(value);
      if (next === value) return;
      value = next; listeners.forEach(notify => notify());
    },
  };
}
// Tab-memory navigation only: no content, proofs, credentials or browser storage.
// The root keeps rendered readers and command controllers outside tab disclosure.
const partitions = new Map<string, ReturnType<typeof cell>>();
function partition(key: string | null) {
  if (key === null) return cell();
  const existing = partitions.get(key);
  if (existing) return existing;
  const next = cell(); partitions.set(key, next);
  return next;
}

export function useRecipientCollectionState(playerId?: string | null) {
  const scope = useCanonicalCollectionScope('recipient');
  const authority = feedbackConversationAuthorityFingerprint();
  const key = scope ? JSON.stringify([scope.actorId, scope.clubId, scope.teamId, scope.seasonId, playerId ?? null, authority]) : null;
  const state = useMemo(() => partition(key), [key]);
  const navigation = useSyncExternalStore(state.subscribe, state.get, state.get);
  const writable = () => !!scope && isCurrentRecapScope(scope) && authority === feedbackConversationAuthorityFingerprint();
  const update = (change: Parameters<typeof state.update>[0]) => { if (writable()) state.update(change); };
  const setControls = (patch: Partial<RecipientCollectionControls>) => update(previous => {
    const controls = { ...previous.controls, ...patch };
    if (controls.limit !== 20 && controls.limit !== 50) return previous;
    if (patch.conversation === 'closed' || patch.recap_seen === 'seen' || patch.recap_read === 'read') controls.attention = 'all';
    if (Object.keys(controls).every(name => controls[name as keyof RecipientCollectionControls] === previous.controls[name as keyof RecipientCollectionControls])) return previous;
    return { ...previous, controls, cursors: [undefined], scrollTop: 0 };
  });
  const query: RecipientFeedbackCollectionQuery = { ...navigation.controls,
    ...(playerId ? { player_id: playerId } : {}), cursor: navigation.cursors.at(-1) };
  return {
    ...navigation, query, setControls,
    next: (cursor: string) => update(previous => !cursor || previous.cursors.includes(cursor) ? previous
      : { ...previous, cursors: [...previous.cursors, cursor], scrollTop: 0 }),
    previous: () => update(previous => previous.cursors.length < 2 ? previous
      : { ...previous, cursors: previous.cursors.slice(0, -1), scrollTop: 0 }),
    setScrollTop: (scrollTop: number) => update(previous => !Number.isFinite(scrollTop) || scrollTop < 0 || previous.scrollTop === scrollTop
      ? previous : { ...previous, scrollTop }),
    resetFilters: () => update(previous => ({ ...initial(), refreshVersion: previous.refreshVersion })),
    refreshFromStart: () => update(previous => ({ ...previous, cursors: [undefined], scrollTop: 0, refreshVersion: previous.refreshVersion + 1 })),
  };
}
